import { useQuery } from "@tanstack/react-query";
import { type ReactElement, type ReactNode } from "react";

import { AssistantAccessContext } from "./assistant-access-context";
import { fetchFlags } from "./feature-flags-api";

/**
 * Cache key for the `fetchFlags()` read.
 *
 * Declared here rather than in `feature-flags-api` because tests mock that
 * module wholesale, and a key that vanished with the mock would let a test
 * decide what the cache identifies.
 */
const featureFlagsKey = (): readonly unknown[] => ["feature-flags"] as const;

/**
 * Feature-flag key gating the ops-console Assistant section (the admin page
 * plus usage-metrics / conversation-logs / guardrail-metrics / rag-quality).
 * Toggle it per environment from the Feature Flags page; the value is read
 * here against the ops console's own build env.
 */
export const ASSISTANT_CONSOLE_FLAG_KEY = "assistant-console";

function readOpsConsoleEnv(): string {
  const raw = (import.meta.env.VITE_ENV_NAME ?? "dev").toLowerCase();
  // Match the other ops-console env readers (EnvBadge, feature-flags/environments):
  // a "production" build maps to the "prod" key. ENR-987 will consolidate all of
  // these onto a shared lib/env.readEnvName() once it lands.
  return raw === "production" ? "prod" : raw;
}

/**
 * Resolves the `assistant-console` flag at the top of the route tree and
 * broadcasts the boolean via {@link AssistantAccessContext}. Reads the admin
 * `fetchFlags()` list (no extra evaluation endpoint) and mirrors the server's
 * `/evaluate` precedence: a per-environment override wins over the flag's
 * global `enabled`.
 *
 * NOT resolved once. The app's Query defaults are `staleTime: 0` with
 * `refetchOnWindowFocus`, so this revalidates after mount and the gate can
 * change mid-session: a list that no longer carries the flag closes the
 * Assistant, and `RequireAssistant` then renders NotFound under an operator
 * already on one of its routes. That is the flag doing its job, but it is a
 * live transition rather than a value fixed at load.
 *
 * Defaults to `false` (Assistant hidden) while loading, when the flag does not
 * exist yet, and when the read fails, including a failed revalidation. That is
 * the safe default, since the Assistant's code-RAG backend (OpenSearch) is
 * being retired (ENR-985). To surface the section in an environment where the
 * backend is live, enable the flag for that env on the Feature Flags page.
 */
export function AssistantAccessProvider({ children }: { children: ReactNode }): ReactElement {
  const query = useQuery({ queryKey: featureFlagsKey(), queryFn: fetchFlags });
  /* A failed read means "no flag", including a failed REVALIDATION. v5 keeps the
     previous `data` on a refetch error, which would hold the Assistant open on
     the strength of a list the console can no longer confirm. Discarding it
     keeps the documented safe default below true of every fetch, not just the
     first. */
  const data = query.isError ? undefined : query.data;

  const flag = data?.find((f) => f.flagKey === ASSISTANT_CONSOLE_FLAG_KEY);
  /* Undefined data covers loading AND failure, so both default the Assistant
     off without a `cancelled` flag deciding which state won the race. */
  const enabled =
    flag === undefined ? false : (flag.enabledByEnvironment?.[readOpsConsoleEnv()] ?? flag.enabled);

  return (
    <AssistantAccessContext.Provider value={enabled}>{children}</AssistantAccessContext.Provider>
  );
}
